import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import './DataIntegrityPanel.css';

/**
 * Dev-only panel for validating data integrity across UI surfaces.
 * 
 * Enable: localStorage.setItem('dh.debug.integrity', '1')
 * Disable: localStorage.removeItem('dh.debug.integrity')
 */
const DataIntegrityPanel = ({ selectedCourseId }) => {
  const { state, status, selectors } = useData();
  const [expanded, setExpanded] = useState(true);
  const [chaosDeleteId, setChaosDeleteId] = useState('');

  if (process.env.NODE_ENV === 'production' || localStorage.getItem('dh.debug.integrity') !== '1') {
    return null;
  }

  // Calculate canonical counts from selectors
  const allAssignments = Object.values(state.assignmentsById).filter(a => !a.isDeleted);
  const allEvents = Object.values(state.eventsById).filter(e => !e.isDeleted);
  const allDocuments = Object.values(state.documentsById).filter(d => !d.isDeleted);
  
  let courseData = null;
  if (selectedCourseId) {
    const progress = selectors.selectCourseProgress(state, selectedCourseId);
    const assignments = selectors.selectAssignmentsByCourse(state, selectedCourseId);
    const events = selectors.selectEventsByCourse(state, selectedCourseId);
    const documents = selectors.selectDocumentsByCourse(state, selectedCourseId);
    
    courseData = {
      courseId: selectedCourseId,
      progress,
      assignments,
      events,
      documents,
    };
  }

  const pendingWrites = Object.entries(state.pendingWritesById || {}).map(([id, meta]) => ({
    id,
    ...meta,
  }));

  const handleChaosToggle = (flag) => {
    const current = localStorage.getItem(flag);
    if (current === '1') {
      localStorage.removeItem(flag);
    } else {
      localStorage.setItem(flag, '1');
    }
    window.location.reload(); // Reload to apply chaos settings
  };

  const handleChaosDelete = () => {
    if (chaosDeleteId.trim()) {
      localStorage.setItem('dh.chaos.softDeleteId', chaosDeleteId.trim());
      window.location.reload();
    }
  };

  const handleClearCache = () => {
    localStorage.setItem('dh.debug.clearCache', '1');
    window.location.reload();
  };

  return (
    <div className="data-integrity-panel">
      <div className="panel-header" onClick={() => setExpanded(!expanded)}>
        <h3>🔍 Data Integrity Panel</h3>
        <button className="toggle-btn">{expanded ? '▼' : '▶'}</button>
      </div>

      {expanded && (
        <div className="panel-content">
          {/* Global Status */}
          <section className="panel-section">
            <h4>Status</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Data Version:</span>
                <span className="value">{state.dataVersion}</span>
              </div>
              <div className="status-item">
                <span className="label">Hydrating:</span>
                <span className={`value ${status.isHydrating ? 'warning' : ''}`}>
                  {status.isHydrating ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Refreshing:</span>
                <span className={`value ${status.isRefreshing ? 'warning' : ''}`}>
                  {status.isRefreshing ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Last Updated:</span>
                <span className="value">
                  {status.lastUpdatedAt ? new Date(status.lastUpdatedAt).toLocaleTimeString() : 'Never'}
                </span>
              </div>
            </div>
          </section>

          {/* Global Canonical Counts */}
          <section className="panel-section">
            <h4>Global Canonical Counts</h4>
            <div className="counts-grid">
              <div className="count-item">
                <span className="label">Assignments:</span>
                <span className="value">{allAssignments.length}</span>
              </div>
              <div className="count-item">
                <span className="label">Events:</span>
                <span className="value">{allEvents.length}</span>
              </div>
              <div className="count-item">
                <span className="label">Documents:</span>
                <span className="value">{allDocuments.length}</span>
              </div>
              <div className="count-item">
                <span className="label">Courses:</span>
                <span className="value">{Object.keys(state.coursesById).length}</span>
              </div>
            </div>
          </section>

          {/* Course-Specific Data */}
          {courseData && (
            <section className="panel-section">
              <h4>Course: {courseData.courseId}</h4>
              <div className="course-data">
                <div className="data-row">
                  <span className="label">Progress:</span>
                  <span className="value">
                    {courseData.progress.completed} / {courseData.progress.total} 
                    ({courseData.progress.percent.toFixed(1)}%)
                  </span>
                </div>
                <div className="data-row">
                  <span className="label">Assignments:</span>
                  <span className="value">{courseData.assignments.length}</span>
                </div>
                <div className="data-row">
                  <span className="label">Events:</span>
                  <span className="value">{courseData.events.length}</span>
                </div>
                <div className="data-row">
                  <span className="label">Documents:</span>
                  <span className="value">{courseData.documents.length}</span>
                </div>
              </div>
            </section>
          )}

          {/* Pending Writes */}
          {pendingWrites.length > 0 && (
            <section className="panel-section">
              <h4 className="warning">Pending Writes ({pendingWrites.length})</h4>
              <div className="pending-writes">
                {pendingWrites.map(pw => (
                  <div key={pw.id} className="pending-write">
                    <span className="type">{pw.entityType}</span>
                    <span className="id">{pw.id}</span>
                    <span className="seq">seq: {pw.seq}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Chaos Controls */}
          <section className="panel-section chaos-section">
            <h4>🔥 Chaos Mode Controls</h4>
            <div className="chaos-controls">
              <button
                className={`chaos-btn ${localStorage.getItem('dh.chaos.failNext') === '1' ? 'active' : ''}`}
                onClick={() => handleChaosToggle('dh.chaos.failNext')}
              >
                Fail Next Update
              </button>
              <button
                className={`chaos-btn ${localStorage.getItem('dh.chaos.outOfOrder') === '1' ? 'active' : ''}`}
                onClick={() => handleChaosToggle('dh.chaos.outOfOrder')}
              >
                Out-of-Order Responses
              </button>
              <div className="chaos-input-group">
                <input
                  type="text"
                  placeholder="Assignment ID to soft delete"
                  value={chaosDeleteId}
                  onChange={(e) => setChaosDeleteId(e.target.value)}
                  className="chaos-input"
                />
                <button onClick={handleChaosDelete} className="chaos-btn">
                  Trigger Soft Delete
                </button>
              </div>
              <button onClick={handleClearCache} className="chaos-btn danger">
                Clear All Cache
              </button>
            </div>
          </section>

          {/* Instructions */}
          <section className="panel-section instructions">
            <p className="hint">
              💡 This panel shows canonical state from selectors. Compare with UI to detect drift.
            </p>
            <p className="hint">
              To disable: <code>localStorage.removeItem('dh.debug.integrity')</code>
            </p>
          </section>
        </div>
      )}
    </div>
  );
};

export default DataIntegrityPanel;
